import { useMemo } from 'react';
import {
  PLATFORM_PRODUCTS,
  getPlatformCatalog,
  labelForPlatformKey,
} from '../../constants/platformPermissions';

export function usePlatformPermissionsByProduct(
  platformPermissions,
  isPermissionHiddenFromViewer
) {
  return useMemo(
    () =>
      PLATFORM_PRODUCTS.map((product) => {
        const catalog = getPlatformCatalog(product.id);
        const permissions = (platformPermissions || [])
          .filter((p) => (p.platform || p.product_id) === product.id)
          .filter((p) => !isPermissionHiddenFromViewer?.(p));
        return {
          ...product,
          catalog,
          permissions,
          keyCount:
            catalog.pages.length +
            catalog.capabilities.length +
            catalog.pageConfigurations.length,
        };
      }),
    [platformPermissions, isPermissionHiddenFromViewer]
  );
}

export function sortPlatformPermissions(permissions) {
  const rank = {
    platform_access: 0,
    page_access: 1,
    page_configuration: 2,
    capability: 3,
  };
  return [...(permissions || [])].sort((a, b) => {
    const ra = rank[a.permission_type] ?? 9;
    const rb = rank[b.permission_type] ?? 9;
    if (ra !== rb) return ra - rb;
    return String(a.resource_id || '').localeCompare(String(b.resource_id || ''));
  });
}

function summarizeVendorRules(rules) {
  if (!rules || typeof rules !== 'object') return null;
  const gpN = Array.isArray(rules.vendor_groups_periods_ids)
    ? rules.vendor_groups_periods_ids.length
    : 0;
  const groupN = Array.isArray(rules.vendor_group_ids) ? rules.vendor_group_ids.length : 0;
  if (!gpN && !groupN) return null;
  const bits = [];
  if (gpN) bits.push(`vendor GP ×${gpN}`);
  if (groupN) bits.push(`vendor group ×${groupN}`);
  return bits.join(', ');
}

function summarizeAcuityLearnRoleMetadata(metadata, field) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta[field]) ? meta[field] : []).map((r) =>
      String(r || '').trim().toLowerCase()
    )
  );
  const bits = [];
  if (roles.has('super_admins') || roles.has('super_admin')) bits.push('super admins');
  if (roles.has('admins') || roles.has('admin')) bits.push('admins');
  if (roles.has('teachers') || roles.has('teacher')) bits.push('teachers');
  if (roles.has('students') || roles.has('student')) bits.push('students');
  if (roles.has('curriculum_developers') || roles.has('curriculum_developer')) {
    bits.push('curriculum developers');
  }
  if (roles.has('counselors') || roles.has('counselor')) bits.push('counselors');
  if (roles.has('parents') || roles.has('parent')) bits.push('parents');
  return bits.length ? bits.join(', ') : null;
}

function summarizeSchoolAddUsersMetadata(metadata) {
  return summarizeAcuityLearnRoleMetadata(metadata, 'add_roles');
}

function summarizeInstructAssignedMetadata(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'instructor_kit')) {
    return 'propose activities, edit activities, grade activities, override activities';
  }
  const verbs = new Set(
    (Array.isArray(meta.instructor_kit) ? meta.instructor_kit : []).map((r) =>
      String(r || '').trim().toLowerCase()
    )
  );
  const bits = [];
  if (verbs.has('propose_activities') || meta.propose_activities === true) bits.push('propose activities');
  if (verbs.has('edit_activities') || meta.edit_activities === true) bits.push('edit activities');
  if (verbs.has('grade_activities') || meta.grade_activities === true) bits.push('grade activities');
  if (verbs.has('override_activities') || meta.override_activities === true) bits.push('override activities');
  return bits.length ? bits.join(', ') : 'no instructor verbs';
}

function summarizeSupervisorKitMetadata(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const field = Object.prototype.hasOwnProperty.call(meta, 'supervisor_kit')
    ? 'supervisor_kit'
    : Object.prototype.hasOwnProperty.call(meta, 'instructor_kit')
      ? 'instructor_kit'
      : null;
  if (!field) {
    return 'grade papers, view papers, override attempts, student chat, staff chat';
  }
  const verbs = new Set(
    (Array.isArray(meta[field]) ? meta[field] : []).map((r) =>
      String(r || '').trim().toLowerCase()
    )
  );
  const bits = [];
  if (verbs.has('grade_activities')) bits.push('grade papers');
  if (verbs.has('view_activities') && !verbs.has('grade_activities')) bits.push('view papers');
  if (verbs.has('override_activities')) bits.push('override attempts');
  if (verbs.has('student_progress_chat')) bits.push('student chat');
  if (verbs.has('staff_progress_chat')) bits.push('staff chat');
  return bits.length ? bits.join(', ') : 'no supervisor verbs';
}

function summarizeLibrarySourceKitMetadata(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'source_kit')) {
    return 'all kinds, replace file, edit analysis';
  }
  const verbs = new Set(
    (Array.isArray(meta.source_kit) ? meta.source_kit : []).map((r) =>
      String(r || '').trim().toLowerCase()
    )
  );
  const bits = [];
  for (const key of ['document', 'image', 'audio', 'video', 'url']) {
    if (verbs.has(key)) bits.push(key);
  }
  if (verbs.has('replace_file')) bits.push('replace file');
  if (verbs.has('edit_analysis')) bits.push('edit analysis');
  for (const key of ['max_12mb', 'max_80mb', 'max_200mb']) {
    if (verbs.has(key)) bits.push(key.replace('max_', ''));
  }
  return bits.length ? bits.join(', ') : 'no source kit';
}

function summarizePeopleListDirectoryMetadata(metadata, rulesFallback) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta.list_roles) ? meta.list_roles : []).map((r) =>
      String(r || '').trim().toLowerCase()
    )
  );
  const rules = rulesFallback && typeof rulesFallback === 'object' ? rulesFallback : {};
  const bits = [];
  if (
    roles.has('students') ||
    roles.has('learners') ||
    meta.list_students === true ||
    rules.list_students === true
  ) {
    bits.push('learners');
  }
  if (
    roles.has('instructors') ||
    meta.list_instructors === true ||
    rules.list_instructors === true
  ) {
    bits.push('instructors');
  }
  if (roles.has('admins') || meta.list_admins === true || rules.list_admins === true) {
    bits.push('admins');
  }
  if (
    roles.has('super_admins') ||
    meta.list_super_admins === true ||
    rules.list_super_admins === true
  ) {
    bits.push('super admins');
  }
  return bits.length ? bits.join(', ') : null;
}

function summarizeCapabilityMetadata(perm) {
  if (!perm) return null;
  if (perm.resource_id === 'people.ListDirectory') {
    return summarizePeopleListDirectoryMetadata(perm.metadata, perm.rules);
  }
  if (
    perm.resource_id === 'messages.ReceiveMessages' ||
    perm.resource_id === 'messages.SendMessages' ||
    perm.resource_id === 'messages.ReplyMessages'
  ) {
    const meta = perm.metadata && typeof perm.metadata === 'object' ? perm.metadata : {};
    if (!Object.prototype.hasOwnProperty.call(meta, 'thread_kinds')) return 'office';
    const kinds = new Set(
      (Array.isArray(meta.thread_kinds) ? meta.thread_kinds : []).map((kind) =>
        String(kind || '').trim().toLowerCase()
      )
    );
    return kinds.has('office') ? 'office' : 'no office threads';
  }
  if (perm.resource_id === 'school.AddUsers') {
    return summarizeSchoolAddUsersMetadata(perm.metadata);
  }
  if (perm.resource_id === 'dm.ViewOthersInboxes') {
    return summarizeAcuityLearnRoleMetadata(perm.metadata, 'inbox_roles') || 'no inboxes';
  }
  if (perm.resource_id === 'school.ViewUsers') {
    return summarizeAcuityLearnRoleMetadata(perm.metadata, 'view_roles');
  }
  if (perm.resource_id === 'course.TeachEnrolled') {
    return summarizeInstructAssignedMetadata(perm.metadata);
  }
  if (perm.resource_id === 'course.TeachWithoutEnrollment') {
    return summarizeSupervisorKitMetadata(perm.metadata);
  }
  if (perm.resource_id === 'library.EditSources' || perm.resource_id === 'library.ProposeSources') {
    return summarizeLibrarySourceKitMetadata(perm.metadata);
  }
  return null;
}

/** Details column for platform grants (type + catalog key). */
export function formatPlatformPermissionDetails(productId, perm) {
  if (!perm) return '—';
  const vendorSummary = summarizeVendorRules(perm.rules);
  const peopleSlices = summarizeCapabilityMetadata(perm);
  if (perm.permission_type === 'platform_access') {
    const accessVal = perm.access;
    const isDeny = accessVal === false || accessVal === 'false';
    const base = isDeny ? 'platform_access · deny' : 'platform_access · allow';
    return vendorSummary ? `${base} · ${vendorSummary}` : base;
  }
  const keyLabel = labelForPlatformKey(productId, perm.permission_type, perm.resource_id);
  const accessVal = perm.access;
  const isDeny =
    (perm.permission_type === 'page_access' || perm.permission_type === 'capability') &&
    (accessVal === false || accessVal === 'false');
  const parts = [
    perm.permission_type,
    keyLabel || perm.resource_id,
    isDeny ? 'deny' : null,
    peopleSlices,
    vendorSummary,
  ].filter(Boolean);
  return parts.join(' · ');
}
